import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Drawer,
  IconButton,
  InputAdornment,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import * as Sentry from '@sentry/react';
import api from 'api';
import { components } from 'api/types';

type RunningTasksResult = components['schemas']['WorkerRunningTasksResult'];
type RunningTask = components['schemas']['WorkerRunningTask'];

const REFRESH_INTERVAL_MS = 30_000;

const COLUMN_WIDTHS = ['26%', '10%', '21%', '8%', '9%', '11%', '10%', '5%'];

function ColumnWidths() {
  return (
    <colgroup>
      {COLUMN_WIDTHS.map((width, idx) => (
        <col key={idx} style={{ width }} />
      ))}
    </colgroup>
  );
}

interface RunningTasksDrawerProps {
  workerId: string;
  workerName: string | null;
  onClose: () => void;
}

function formatDuration(ms: number): string {
  if (ms < 0) return '0s';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function formatDurationRange(minMs: number, maxMs: number): string {
  const min = formatDuration(minMs);
  const max = formatDuration(maxMs);
  return min === max ? max : `${min} – ${max}`;
}

function taskAgeMs(task: RunningTask, now: number): number {
  return task.running_since ? now - new Date(task.running_since).getTime() : 0;
}

interface TaskGroup {
  key: string;
  wfId: string;
  wfName: string;
  taskName: string;
  projectId: string | null;
  tasks: RunningTask[];
  maxAgeMs: number;
  minAgeMs: number;
  anomalyCount: number;
}

// Branch/while tasks execute on the backend: the batched checkout path never
// delivers them to the worker, so the worker (correctly) never reports them.
function isBackendHandled(task: RunningTask): boolean {
  return task.task_type === 'branch' || task.task_type === 'while';
}

// Mirrors the backend sweeps' grace: a just-started task is legitimately absent
// from the worker's last checkin self-report until the next checkin lands.
function reportedGraceMs(checkinInterval: number): number {
  return Math.max(3 * checkinInterval, 60) * 1000;
}

function isUnreportedAnomaly(task: RunningTask, now: number, checkinInterval: number): boolean {
  return !task.reported && !isBackendHandled(task) && taskAgeMs(task, now) > reportedGraceMs(checkinInterval);
}

function isAnomaly(task: RunningTask, now: number, checkinInterval: number): boolean {
  return (
    isUnreportedAnomaly(task, now, checkinInterval) ||
    task.worker_lost ||
    task.cancellation_requested ||
    task.kill_requested ||
    task.state !== 'RUNNING'
  );
}

function groupTasks(tasks: RunningTask[], now: number, checkinInterval: number): TaskGroup[] {
  const byKey = new Map<string, RunningTask[]>();
  for (const task of tasks) {
    const key = `${task.wf_id}::${task.task_name ?? ''}`;
    const list = byKey.get(key);
    if (list) list.push(task);
    else byKey.set(key, [task]);
  }
  const groups: TaskGroup[] = [];
  for (const [key, members] of byKey) {
    const ages = members.map((t) => taskAgeMs(t, now)).sort((a, b) => a - b);
    members.sort((a, b) => taskAgeMs(b, now) - taskAgeMs(a, now));
    const first = members[0];
    groups.push({
      key,
      wfId: first.wf_id,
      wfName: first.wf_name ?? first.wf_id,
      taskName: first.task_name ?? '(unnamed task)',
      projectId: first.project_id ?? null,
      tasks: members,
      maxAgeMs: ages[ages.length - 1],
      minAgeMs: ages[0],
      anomalyCount: members.filter((t) => isAnomaly(t, now, checkinInterval)).length,
    });
  }
  groups.sort((a, b) => b.maxAgeMs - a.maxAgeMs);
  return groups;
}

interface WorkflowSection {
  wfId: string;
  wfName: string;
  projectId: string | null;
  groups: TaskGroup[];
  taskCount: number;
  anomalyCount: number;
  maxAgeMs: number;
}

function sectionByWorkflow(groups: TaskGroup[]): WorkflowSection[] {
  const byWf = new Map<string, WorkflowSection>();
  for (const group of groups) {
    let section = byWf.get(group.wfId);
    if (!section) {
      section = {
        wfId: group.wfId,
        wfName: group.wfName,
        projectId: group.projectId,
        groups: [],
        taskCount: 0,
        anomalyCount: 0,
        maxAgeMs: 0,
      };
      byWf.set(group.wfId, section);
    }
    section.groups.push(group);
    section.taskCount += group.tasks.length;
    section.anomalyCount += group.anomalyCount;
    section.maxAgeMs = Math.max(section.maxAgeMs, group.maxAgeMs);
  }
  return [...byWf.values()].sort((a, b) => b.maxAgeMs - a.maxAgeMs);
}

interface TaskSignal {
  cpuPercent: number | null;
  rssBytes: number | null;
  lastWriteMs: number | null;
  progressMs: number | null;
  sampledMs: number | null;
}

function taskSignal(task: RunningTask): TaskSignal {
  const vitals = task.vitals as {
    cpu_percent?: number | null;
    rss_bytes?: number | null;
    last_file_write?: string | null;
    sampled_at?: string | null;
  } | null;
  return {
    cpuPercent: vitals?.cpu_percent ?? null,
    rssBytes: vitals?.rss_bytes ?? null,
    lastWriteMs: vitals?.last_file_write ? new Date(vitals.last_file_write).getTime() : null,
    progressMs: task.progress_updated_on ? new Date(task.progress_updated_on).getTime() : null,
    sampledMs: vitals?.sampled_at ? new Date(vitals.sampled_at).getTime() : null,
  };
}

function HeaderCell({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <TableCell>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        {label}
        <Tooltip title={tooltip}>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
        </Tooltip>
      </Box>
    </TableCell>
  );
}

function NoVitalsDash({ vitalsSupported }: { vitalsSupported: boolean }) {
  return (
    <Tooltip
      title={
        vitalsSupported
          ? 'Not sampled yet — vitals arrive after the first sampling interval'
          : 'This worker does not report process vitals (requires a v2 worker on a vitals-capable pytailor)'
      }
    >
      <Typography component="span" variant="body2" color="text.disabled">
        –
      </Typography>
    </Tooltip>
  );
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MB`;
  return `${Math.round(bytes / 1024)} kB`;
}

function TaskRow({
  task,
  now,
  checkinInterval,
  vitalsSupported,
}: {
  task: RunningTask;
  now: number;
  checkinInterval: number;
  vitalsSupported: boolean;
}) {
  const ageMs = taskAgeMs(task, now);
  const backendHandled = isBackendHandled(task);
  const signal = taskSignal(task);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const copyRunDir = (runDir: string) => {
    navigator.clipboard.writeText(runDir).then(
      () => setCopyState('copied'),
      (err) => {
        setCopyState('failed');
        Sentry.captureException(err);
      },
    );
  };

  return (
    <TableRow hover>
      <TableCell sx={{ pl: 6 }}>
        {task.project_id ? (
          <Link component={RouterLink} to={`/projects/${task.project_id}/workflows/${task.wf_id}/details`}>
            {task.task_id}
          </Link>
        ) : (
          task.task_id
        )}
      </TableCell>
      <TableCell>{formatDuration(ageMs)}</TableCell>
      <TableCell>
        <Chip label={task.state} color={task.state === 'RUNNING' ? 'success' : 'info'} size="small" sx={{ mr: 0.5 }} />
        {backendHandled ? (
          <Tooltip title="Branch expansion runs on the backend, not on the worker — worker self-reporting does not apply">
            <Chip label={task.task_type} color="secondary" size="small" variant="outlined" />
          </Tooltip>
        ) : task.reported ? (
          <Tooltip
            title={
              task.loop_task_id
                ? "A chunk runs inside its loop task's process and is never checked out on its own; the worker confirms the loop, which confirms the chunk"
                : 'The worker confirms it is running this task'
            }
          >
            <Chip label="confirmed" color="success" size="small" variant="outlined" />
          </Tooltip>
        ) : isUnreportedAnomaly(task, now, checkinInterval) ? (
          <Tooltip title="The task should be running on this worker, but the worker does not confirm it">
            <Chip label="unconfirmed" color="warning" size="small" />
          </Tooltip>
        ) : (
          <Tooltip title="Just started — the worker's confirmation is expected shortly">
            <Chip label="starting" size="small" variant="outlined" />
          </Tooltip>
        )}
        {task.worker_lost && <Chip label="worker lost" color="error" size="small" sx={{ ml: 0.5 }} />}
        {task.cancellation_requested && <Chip label="cancel pending" color="warning" size="small" sx={{ ml: 0.5 }} />}
        {task.kill_requested && <Chip label="kill pending" color="error" size="small" sx={{ ml: 0.5 }} />}
      </TableCell>
      <TableCell>
        {signal.cpuPercent != null ? (
          <Tooltip title="CPU use of the task's process tree since the previous sample, as % of one core">
            <Typography component="span" variant="body2">
              {signal.cpuPercent}%
            </Typography>
          </Tooltip>
        ) : (
          <NoVitalsDash vitalsSupported={vitalsSupported} />
        )}
      </TableCell>
      <TableCell>
        {signal.rssBytes != null ? (
          <Tooltip title="Resident memory of the task's process tree">
            <Typography component="span" variant="body2">
              {formatBytes(signal.rssBytes)}
            </Typography>
          </Tooltip>
        ) : (
          <NoVitalsDash vitalsSupported={vitalsSupported} />
        )}
      </TableCell>
      <TableCell>
        {signal.lastWriteMs != null ? (
          <Tooltip title="Newest file modification time under the task's run dir">
            <Typography component="span" variant="body2">
              {formatDuration(now - signal.lastWriteMs)} ago
            </Typography>
          </Tooltip>
        ) : signal.sampledMs != null ? (
          <Tooltip title="No files found under the task's run dir at the last sample">
            <Typography component="span" variant="body2" color="text.disabled">
              none
            </Typography>
          </Tooltip>
        ) : (
          <NoVitalsDash vitalsSupported={vitalsSupported} />
        )}
      </TableCell>
      <TableCell>
        {signal.progressMs != null ? (
          <Tooltip
            title={
              task.progress != null ? <pre style={{ margin: 0 }}>{JSON.stringify(task.progress, null, 2)}</pre> : ''
            }
          >
            <Typography component="span" variant="body2">
              {formatDuration(now - signal.progressMs)} ago
            </Typography>
          </Tooltip>
        ) : (
          <Tooltip title="The task has not reported progress (progress reporting is opt-in from the task function)">
            <Typography component="span" variant="body2" color="text.disabled">
              –
            </Typography>
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        {task.run_dir && (
          <Tooltip
            title={
              copyState === 'copied'
                ? 'Copied run dir'
                : copyState === 'failed'
                  ? 'Could not copy — select the path manually'
                  : `Copy run dir: ${task.run_dir}`
            }
          >
            <IconButton
              size="small"
              color={copyState === 'failed' ? 'error' : 'default'}
              aria-label="Copy run dir"
              onMouseLeave={() => setCopyState('idle')}
              onClick={() => copyRunDir(task.run_dir!)}
            >
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}

export default function RunningTasksDrawer({ workerId, workerName, onClose }: RunningTasksDrawerProps) {
  const [result, setResult] = useState<RunningTasksResult | null>(null);
  const [now, setNow] = useState(0);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [groupToggles, setGroupToggles] = useState<Map<string, boolean>>(new Map());

  const fetchTasks = useCallback(() => {
    api
      .get(`workers/${workerId}/running-tasks`)
      .json()
      .then(
        (data) => {
          setResult(data as RunningTasksResult);
          setNow(Date.now());
          setError(false);
        },
        (err) => {
          Sentry.captureException(err);
          setError(true);
        },
      );
  }, [workerId]);

  useEffect(() => {
    fetchTasks();
    const interval = setInterval(fetchTasks, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchTasks]);

  const filteredTasks = useMemo(() => {
    if (!result) return [];
    const query = search.trim().toLowerCase();
    if (!query) return result.tasks;
    return result.tasks.filter(
      (t) =>
        t.task_id.toLowerCase().includes(query) ||
        (t.task_name ?? '').toLowerCase().includes(query) ||
        (t.wf_name ?? '').toLowerCase().includes(query) ||
        t.wf_id.toLowerCase().includes(query),
    );
  }, [result, search]);

  // now is intentionally not a dependency: groups recompute on data/search
  // changes, and ages refresh with the 30s poll cycle.
  const checkinInterval = result?.worker.checkin_interval ?? 0;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const groups = useMemo(() => groupTasks(filteredTasks, now, checkinInterval), [filteredTasks, checkinInterval]);
  const sections = useMemo(() => sectionByWorkflow(groups), [groups]);

  // Anomaly groups start expanded; an explicit user toggle wins from then on.
  const isExpanded = (group: TaskGroup) => groupToggles.get(group.key) ?? group.anomalyCount > 0;

  const toggleGroup = (group: TaskGroup) => {
    const next = !isExpanded(group);
    setGroupToggles((prev) => new Map(prev).set(group.key, next));
  };

  const notReported = result ? result.tasks.filter((t) => isUnreportedAnomaly(t, now, checkinInterval)).length : 0;

  const unknownReportedIds = result?.unknown_reported_ids ?? [];
  const oldestMs = result && result.tasks.length ? Math.max(...result.tasks.map((t) => taskAgeMs(t, now))) : 0;

  return (
    <Drawer anchor="right" open onClose={onClose} slotProps={{ paper: { sx: { width: { xs: '100%', md: '60%' } } } }}>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Running tasks — {workerName ?? workerId.substring(0, 8)}
          </Typography>
          <Tooltip title="Refresh now (auto-refreshes every 30s)">
            <IconButton onClick={fetchTasks} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {result && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', my: 1 }}>
            <Chip
              label={`${result.tasks.length} active (${result.worker.running}/${result.worker.slots}${result.worker.overshoot ? ` +${result.worker.overshoot}` : ''} slots)`}
              size="small"
            />
            {result.tasks.length > 0 && <Chip label={`oldest ${formatDuration(oldestMs)}`} size="small" />}
            {notReported > 0 && <Chip label={`${notReported} unconfirmed`} color="warning" size="small" />}
            {unknownReportedIds.length > 0 && (
              <Tooltip
                title={
                  'The worker says it is running these tasks, but they are not active in the database ' +
                  `(typically they just finished): ${unknownReportedIds.join(', ')}`
                }
              >
                <Chip label={`${unknownReportedIds.length} unknown tasks`} color="default" size="small" />
              </Tooltip>
            )}
          </Box>
        )}

        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by task id, task name or workflow"
          size="small"
          sx={{ mb: 1 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />

        {error && <Alert severity="error">Failed to load running tasks.</Alert>}
        {!result && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {result && (
          <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
            <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
              <ColumnWidths />
              <TableHead>
                <TableRow>
                  <HeaderCell label="Workflow / task" tooltip="Tasks grouped per workflow and task name" />
                  <HeaderCell label="Running for" tooltip="Time since the task entered the RUNNING state" />
                  <HeaderCell
                    label="Status"
                    tooltip="The task's state, and whether the worker confirms it is actually running the task"
                  />
                  <HeaderCell
                    label="CPU"
                    tooltip="CPU use of the task's process tree (including subprocesses) over the last sampling interval, as % of one core — a multithreaded task can exceed 100%. Sampled by the worker roughly once a minute."
                  />
                  <HeaderCell
                    label="Mem"
                    tooltip="Resident memory (RSS) of the task's process tree at the last sample"
                  />
                  <HeaderCell
                    label="Last write"
                    tooltip="Time since the task last wrote to a file in its run directory"
                  />
                  <HeaderCell
                    label="Progress"
                    tooltip="How long since the task reported progress. Only tasks that use Tailor progress reporting show a value here."
                  />
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        {search ? 'No tasks match the filter.' : 'No active tasks on this worker.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {sections.map((section) => (
                  <Fragment key={section.wfId}>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell colSpan={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {section.projectId ? (
                              <Link
                                component={RouterLink}
                                to={`/projects/${section.projectId}/workflows/${section.wfId}/details`}
                              >
                                {section.wfName}
                              </Link>
                            ) : (
                              section.wfName
                            )}{' '}
                            <Typography component="span" variant="body2" color="text.secondary">
                              #{section.wfId}
                            </Typography>
                          </Typography>
                          <Chip label={`${section.taskCount} task${section.taskCount === 1 ? '' : 's'}`} size="small" />
                          {section.anomalyCount > 0 && (
                            <Chip label={`${section.anomalyCount} ⚠`} color="warning" size="small" />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    {section.groups.map((group) => (
                      <Fragment key={group.key}>
                        <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleGroup(group)}>
                          <TableCell sx={{ pl: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                sx={{ p: 0 }}
                                aria-expanded={isExpanded(group)}
                                aria-label={`${isExpanded(group) ? 'Collapse' : 'Expand'} ${group.taskName}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleGroup(group);
                                }}
                              >
                                {isExpanded(group) ? (
                                  <ExpandMoreIcon fontSize="small" />
                                ) : (
                                  <ChevronRightIcon fontSize="small" />
                                )}
                              </IconButton>
                              <Typography variant="body2">{group.taskName}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{formatDurationRange(group.minAgeMs, group.maxAgeMs)}</TableCell>
                          <TableCell>
                            <Chip label={`×${group.tasks.length}`} size="small" />
                            {group.anomalyCount > 0 && (
                              <Chip label={`${group.anomalyCount} ⚠`} color="warning" size="small" sx={{ ml: 0.5 }} />
                            )}
                          </TableCell>
                          <TableCell />
                          <TableCell />
                          <TableCell />
                          <TableCell />
                          <TableCell />
                        </TableRow>
                        {isExpanded(group) && (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                              <Collapse in unmountOnExit>
                                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                  <ColumnWidths />
                                  <TableBody>
                                    {group.tasks.map((task) => (
                                      <TaskRow
                                        key={task.task_id}
                                        task={task}
                                        now={now}
                                        checkinInterval={checkinInterval}
                                        vitalsSupported={result.vitals_supported ?? false}
                                      />
                                    ))}
                                  </TableBody>
                                </Table>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ))}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
